import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IEvent, CALENDAR_KEYS } from '@/types/admin/events';
import { ADMIN_COLORS } from '@/constants/admin';
import { formatEventType, generateDayEventList } from '@/hooks/admin/useEvents';
import { LazyImage } from '@/components/common/admin/mobile/LazyImage';
import { EventDetailsModal } from './EventDetailsModal';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';

interface EventsCalendarProps {
  monthlyEvents: IEvent[];
  selectedMonth: Date;
  onMonthChange: (month: Date) => void;
  onEventSelect?: (event: IEvent) => void;
  onEditEvent?: (eventId: number) => void;
  // Desktop-only options: hide internal events list and disable modal behavior
  showEventsList?: boolean;
  modalBehavior?: 'modal' | 'none';
}

export const EventsCalendar: React.FC<EventsCalendarProps> = ({
  monthlyEvents,
  selectedMonth,
  onMonthChange,
  onEventSelect,
  onEditEvent,
  showEventsList = true,
  modalBehavior = 'modal',
}) => {
  const [currentDate, setCurrentDate] = useState(selectedMonth);
  const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  
  // Keep internal currentDate in sync if parent selectedMonth changes externally
  useEffect(() => {
    if (
      selectedMonth.getFullYear() !== currentDate.getFullYear() ||
      selectedMonth.getMonth() !== currentDate.getMonth()
    ) {
      setCurrentDate(selectedMonth);
    }
  }, [selectedMonth, currentDate]);

  // Generate calendar grid
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    // Create calendar grid
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    // Calculate number of weeks needed
    const totalCells = startingDayOfWeek + daysInMonth;
    const weeksNeeded = Math.ceil(totalCells / 7);
    
    return { days, weeksNeeded };
  }, [currentDate]);
  
  // Get events for each day
  const dayEventPairings = useMemo(() => {
    return generateDayEventList(monthlyEvents);
  }, [monthlyEvents]);

  // Handle event selection
  const handleEventSelect = (event: IEvent) => {
    if (modalBehavior === 'modal') {
      setSelectedEvent(event);
      setModalVisible(true);
    }
    onEventSelect?.(event);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedEvent(null);
  };

  const handleEditEvent = (eventId: number) => {
    setModalVisible(false);
    setSelectedEvent(null);
    onEditEvent?.(eventId);
  };
  
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
    onMonthChange(newDate);
  };
  
  const getEventsForDay = (date: Date | null) => {
    if (!date) return [];
    const dateKey = date.toISOString().slice(0, 10);
    return dayEventPairings[dateKey] || [];
  };
  
  const renderCalendarDay = (date: Date | null, index: number) => {
    if (!date) {
      return <View key={index} style={styles.emptyDay} />;
    }
    
    const events = getEventsForDay(date);
    const isToday = date.toDateString() === new Date().toDateString();
    
    return (
      <TouchableOpacity
        key={index}
        style={[
          styles.calendarDay,
          isToday && styles.todayDay,
        ]}
        onPress={() => {
          if (events.length > 0) {
            handleEventSelect(events[0]);
          }
        }}
      >
        <Text style={[
          styles.dayNumber,
          isToday && styles.todayText,
        ]}>
          {date.getDate()}
        </Text>
        
        {/* Event indicator - single dot per day with event type color */}
        {events.length > 0 && (
          <View style={styles.eventIndicators}>
            {(() => {
              // Get the most recent/important event for color
              const primaryEvent = events[0];
              const eventType = formatEventType(primaryEvent);
              const eventKey = CALENDAR_KEYS.find(key => key.title === eventType);
              return (
                <View
                  style={[
                    styles.eventDot,
                    { backgroundColor: eventKey?.color || ADMIN_COLORS.TEXT_SECONDARY }
                  ]}
                />
              );
            })()}
            {events.length > 1 && (
              <Text style={styles.moreEventsText}>{events.length}</Text>
            )}
          </View>
        )}
      </TouchableOpacity>
    );
  };
  
  const renderEventsList = () => {
    const sortedDays = Object.keys(dayEventPairings)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
      .filter(day => {
        const dayDate = new Date(day);
        return dayDate.getMonth() === currentDate.getMonth() && 
               dayDate.getFullYear() === currentDate.getFullYear();
      });
    
    if (sortedDays.length === 0) {
      return (
        <View style={styles.noEventsContainer}>
          <Text style={styles.noEventsText}>No events this month</Text>
        </View>
      );
    }
    
    return (
      <View style={styles.eventsList}>
        {sortedDays.map(day => {
          const events = dayEventPairings[day];
          const dayDate = new Date(day);
          
          return (
            <View key={day} style={styles.dayEventsContainer}>
              <Text style={styles.dayHeader}>
                {dayDate.toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric',
                  timeZone: 'UTC'
                })}
              </Text>
              {events.map(event => {
                const eventType = formatEventType(event);
                const eventKey = CALENDAR_KEYS.find(key => key.title === eventType);
                
                return (
                  <TouchableOpacity
                    key={event.id}
                    style={styles.eventItem}
                    onPress={() => handleEventSelect(event)}
                  >
                    <View style={styles.eventItemContent}>
                      {/* Event Image */}
                      <View style={styles.eventImageContainer}>
                        {(() => {
                          const firstPhotoUrl = event.photos?.[0]?.photoUrl;
                          if (firstPhotoUrl) {
                            return (
                              <LazyImage
                                source={{ uri: firstPhotoUrl }}
                                style={styles.eventImage}
                                resizeMode="cover"
                                placeholder={
                                  <View style={styles.eventImagePlaceholder}>
                                    <Text style={styles.eventImagePlaceholderText}>📅</Text>
                                  </View>
                                }
                                errorPlaceholder={
                                  <View style={styles.eventImagePlaceholder}>
                                    <Text style={styles.eventImagePlaceholderText}>❌</Text>
                                  </View>
                                }
                              />
                            );
                          }
                          return (
                          <View style={styles.eventImagePlaceholder}>
                            <Text style={styles.eventImagePlaceholderText}>📅</Text>
                          </View>
                          );
                        })()}
                      </View>
                      
                      {/* Event Details */}
                      <View style={styles.eventDetails}>
                        <View style={styles.eventItemHeader}>
                          <View style={styles.eventItemLeft}>
                            <View
                              style={[
                                styles.eventStatusDot,
                                { backgroundColor: eventKey?.color || ADMIN_COLORS.TEXT_SECONDARY }
                              ]}
                            />
                            <Text style={styles.eventName} numberOfLines={2}>{event.name}</Text>
                          </View>
                          <Text style={styles.eventTime}>
                            {new Date(event.startTime).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </Text>
                        </View>
                        <Text style={styles.eventRsvps}>
                          {event.n_rsvps} RSVPs
                        </Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          );
        })}
      </View>
    );
  };
  
  return (
    <ScrollView 
      style={styles.container} 
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
    >
      {/* Calendar Header */}
      <View style={styles.calendarHeader}>
        <TouchableOpacity
          style={styles.navButton}
          onPress={() => navigateMonth('prev')}
        >
          <Text style={styles.navButtonText}>‹</Text>
        </TouchableOpacity>
        
        <Text style={styles.monthYearText}>
          {currentDate.toLocaleDateString('en-US', { 
            month: 'long', 
            year: 'numeric' 
          })}
        </Text>
        
        <TouchableOpacity
          style={styles.navButton}
          onPress={() => navigateMonth('next')}
        >
          <Text style={styles.navButtonText}>›</Text>
        </TouchableOpacity>
      </View>
      
      {/* Days of week header */}
      <View style={styles.weekHeader}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <Text key={day} style={styles.weekDay}>{day}</Text>
        ))}
      </View>
      
      {/* Calendar Grid */}
      <View style={[styles.calendarGrid, { height: calendarData.weeksNeeded * 50 }]}>
        {calendarData.days.map((date, index) => renderCalendarDay(date, index))}
      </View>
      
      {/* Status Legend */}
      <View style={styles.legend}>
        {CALENDAR_KEYS.map(key => (
          <View key={key.title} style={styles.legendItem}>
            <View
              style={[
                styles.legendDot,
                { backgroundColor: key.color }
              ]}
            />
            <Text style={styles.legendText}>{key.title}</Text>
          </View>
        ))}
      </View>
      
      {/* Events List */}
      {showEventsList && (
        <View style={styles.eventsListContainer}>
          <Text style={styles.eventsListTitle}>
            {currentDate.toLocaleDateString('en-US', { month: 'long' })} Events
          </Text>
          {renderEventsList()}
        </View>
      )}

      {/* Event Details Modal */}
      {modalBehavior === 'modal' && (
        <AdminErrorBoundary context="EventDetailsModal">
          <EventDetailsModal
            visible={modalVisible}
            event={selectedEvent}
            onClose={handleCloseModal}
            onEdit={handleEditEvent}
          />
        </AdminErrorBoundary>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    // Remove flexGrow to prevent unnecessary expansion
  },
  calendarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    marginBottom: 14,
  },
  navButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  navButtonText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  monthYearText: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  weekHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    marginBottom: 8,
  },
  weekDay: {
    flex: 1,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    padding: 4,
  },
  calendarDay: {
    width: '14.28%', // 100% / 7 days
    height: 50,
    padding: 4,
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderRadius: 4,
  },
  emptyDay: {
    width: '14.28%',
    height: 50,
  },
  todayDay: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '20', // 20% opacity
  },
  dayNumber: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  todayText: {
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '700',
  },
  eventIndicators: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  eventDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginHorizontal: 2,
    marginVertical: 1,
  },
  moreEventsText: {
    fontSize: 8,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    marginLeft: 2,
  },
  legend: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  legendText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  eventsListContainer: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    padding: 16,
    // Remove minHeight to allow natural sizing
  },
  eventsListTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 12,
  },
  eventsList: {
    // Remove flex: 1 since parent ScrollView handles scrolling
  },
  noEventsContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
    // Remove minHeight and reduce padding
  },
  noEventsText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
  },
  dayEventsContainer: {
    marginBottom: 20,
  },
  dayHeader: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 10,
  },
  eventItem: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: ADMIN_COLORS.PRIMARY,
  },
  eventItemContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  eventImageContainer: {
    marginRight: 12,
  },
  eventImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
  },
  eventImagePlaceholder: {
    width: 50,
    height: 50,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    justifyContent: 'center',
    alignItems: 'center',
  },
  eventImagePlaceholderText: {
    fontSize: 20,
  },
  eventDetails: {
    flex: 1,
  },
  eventItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  eventItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  eventStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  eventName: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
    marginRight: 8,
  },
  eventTime: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  eventLocation: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 2,
  },
  eventRsvps: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
});
